/**
 * Layout Gizmo Service
 *
 * Figma-style corner resize handles for objects with a Layout behavior.
 * Dragging handles writes to Layout's width/height/depth (meters) and
 * triggers layout recomputation — transform.scaling is never touched.
 *
 * Ported from AILayout_Prototype's HandleSystem, adapted for Inspector V2.
 */

import type { ServiceDefinition, IGizmoService, ISelectionService } from '@babylonjs/inspector';
import { GizmoServiceIdentity, SelectionServiceIdentity } from '@babylonjs/inspector';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3, Matrix } from '@babylonjs/core/Maths/math.vector';
import { PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';
import type { PointerInfo } from '@babylonjs/core/Events/pointerEvents';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Scene } from '@babylonjs/core/scene';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Layout } from 'trace-viewer/behaviors';
import type { LayoutData } from 'trace-viewer/behaviors';
import { getUndoManager, BehaviorCommitCommand } from 'trace-viewer/editor';
import { EDITOR_EVENTS, dispatchBehaviorChanged } from './editor-events';

// ── Types ────────────────────────────────────────────────────

type Axis = 'X' | 'Y' | 'Z';

interface HandleConfig {
    /** Which axes this handle modifies */
    axes: Axis[];
    /** Direction multiplier for each axis (-1 or 1) */
    directions: number[];
    /** Local offset from center (normalized -1 to 1) */
    offset: Vector3;
}

interface Handle {
    mesh: Mesh;
    config: HandleConfig;
}

// 8 corner handles
const CORNER_CONFIGS: HandleConfig[] = [
    { axes: ['X', 'Y', 'Z'], directions: [1, 1, 1], offset: new Vector3(1, 1, 1) },
    { axes: ['X', 'Y', 'Z'], directions: [-1, 1, 1], offset: new Vector3(-1, 1, 1) },
    { axes: ['X', 'Y', 'Z'], directions: [1, -1, 1], offset: new Vector3(1, -1, 1) },
    { axes: ['X', 'Y', 'Z'], directions: [-1, -1, 1], offset: new Vector3(-1, -1, 1) },
    { axes: ['X', 'Y', 'Z'], directions: [1, 1, -1], offset: new Vector3(1, 1, -1) },
    { axes: ['X', 'Y', 'Z'], directions: [-1, 1, -1], offset: new Vector3(-1, 1, -1) },
    { axes: ['X', 'Y', 'Z'], directions: [1, -1, -1], offset: new Vector3(1, -1, -1) },
    { axes: ['X', 'Y', 'Z'], directions: [-1, -1, -1], offset: new Vector3(-1, -1, -1) },
];

const HANDLE_BASE_SIZE = 0.05;
const MIN_SIZE = 0.001;

// ── Service ──────────────────────────────────────────────────

export const LayoutGizmoServiceDefinition: ServiceDefinition<[], [IGizmoService, ISelectionService]> = {
    friendlyName: 'Layout Resize Handles',
    consumes: [GizmoServiceIdentity, SelectionServiceIdentity],

    factory: (gizmoService: IGizmoService, selectionService: ISelectionService) => {
        let scene: Scene | null = null;
        let handles: Handle[] = [];
        let matNormal: StandardMaterial | null = null;
        let matHover: StandardMaterial | null = null;
        let matActive: StandardMaterial | null = null;

        // Drag state
        let activeHandle: Handle | null = null;
        let dragStartPoint: Vector3 | null = null;
        let dragStartSize: { x: number; y: number; z: number } | null = null;
        let dragLayout: Layout | null = null;
        let dragNode: TransformNode | null = null;
        let dragRotMatrix: Matrix | null = null;
        let dragBeforeData: LayoutData | null = null;
        let cameraWasAttached = false;

        let handleRoot: TransformNode | null = null;
        let pointerObserver: { remove(): void } | null = null;

        // ── Materials ────────────────────────────────────────

        function ensureMaterials(s: Scene): void {
            if (matNormal) return;
            matNormal = new StandardMaterial('__layoutHandleMat__', s);
            matNormal.diffuseColor = new Color3(1, 1, 1);
            matNormal.emissiveColor = new Color3(0.3, 0.3, 0.3);

            matHover = new StandardMaterial('__layoutHandleHoverMat__', s);
            matHover.diffuseColor = new Color3(0.0, 0.8, 1.0);
            matHover.emissiveColor = new Color3(0.0, 0.6, 0.9);
            matHover.disableLighting = true;

            matActive = new StandardMaterial('__layoutHandleActiveMat__', s);
            matActive.diffuseColor = new Color3(0.0, 0.8, 1.0);
            matActive.emissiveColor = new Color3(0.0, 0.5, 0.8);
        }

        // ── Handle creation / teardown ───────────────────────

        function clearHandles(): void {
            for (const h of handles) h.mesh.dispose();
            handles = [];
            if (handleRoot) {
                handleRoot.dispose();
                handleRoot = null;
            }
        }

        function createHandles(s: Scene, node: TransformNode, size: Vector3): void {
            ensureMaterials(s);
            const camera = s.activeCamera;
            const worldPos = node.getAbsolutePosition();
            const rotMatrix = getWorldRotationMatrix(node);

            // Parent node keeps handles organized and hidden from scene explorer
            handleRoot = new TransformNode('__layoutHandles__', s);
            handleRoot.metadata = { doNotSerialize: true, hidden: true };

            for (let idx = 0; idx < CORNER_CONFIGS.length; idx++) {
                const config = CORNER_CONFIGS[idx];
                const localOffset = new Vector3(
                    config.offset.x * size.x / 2,
                    config.offset.y * size.y / 2,
                    config.offset.z * size.z / 2,
                );
                const handlePos = worldPos.add(Vector3.TransformNormal(localOffset, rotMatrix));

                let dynamicSize = HANDLE_BASE_SIZE;
                if (camera) {
                    const dist = Vector3.Distance(camera.position, handlePos);
                    dynamicSize = Math.max(HANDLE_BASE_SIZE, Math.min(HANDLE_BASE_SIZE * 3, dist * 0.05));
                }

                const mesh = MeshBuilder.CreateBox(`__layoutHandle_${idx}__`, { size: dynamicSize }, s);
                mesh.parent = handleRoot;
                mesh.setAbsolutePosition(handlePos);
                mesh.material = matNormal!;
                mesh.isPickable = true;
                mesh.renderingGroupId = 2;
                mesh.metadata = { editorHandle: true, layoutHandle: true };

                handles.push({ mesh, config });
            }
        }

        /** Extract world rotation matrix from a node (ignoring scale/translation). */
        function getWorldRotationMatrix(node: TransformNode): Matrix {
            const world = node.getWorldMatrix();
            // Zero out translation, extract pure rotation+scale, then normalize columns
            const rot = world.clone();
            rot.setTranslation(Vector3.Zero());
            // Normalize each axis column to remove scale
            const row0 = new Vector3(rot.m[0], rot.m[1], rot.m[2]).normalize();
            const row1 = new Vector3(rot.m[4], rot.m[5], rot.m[6]).normalize();
            const row2 = new Vector3(rot.m[8], rot.m[9], rot.m[10]).normalize();
            return Matrix.FromValues(
                row0.x, row0.y, row0.z, 0,
                row1.x, row1.y, row1.z, 0,
                row2.x, row2.y, row2.z, 0,
                0, 0, 0, 1,
            );
        }

        // ── Resolve selected Layout ──────────────────────────

        function getSelectedLayout(): { layout: Layout; node: TransformNode; size: Vector3 } | null {
            const entity = selectionService.selectedEntity;
            if (!entity || !(entity instanceof TransformNode)) return null;

            const traceScene = getUndoManager().traceScene;
            if (!traceScene) return null;

            const so = traceScene.findSceneObjectByTransform(entity);
            if (!so) return null;

            const layout = so.getBehavior(Layout);
            if (!layout) return null;

            const data = layout.getData();
            const w = data.width ?? 1;
            const h = data.height ?? 1;
            const d = data.depth ?? 1;

            return { layout, node: entity, size: new Vector3(w, h, d) };
        }

        // ── Sync handles to current selection ────────────────

        function sync(): void {
            clearHandles();
            const mode = gizmoService.gizmoMode;
            if (mode !== 'boundingBox') return;

            const sel = getSelectedLayout();
            if (!sel) return;

            scene = sel.node.getScene();
            ensureMaterials(scene);

            createHandles(scene, sel.node, sel.size);

            // Ensure pointer observer is set up
            if (!pointerObserver) {
                pointerObserver = scene.onPointerObservable.add((info: PointerInfo) => {
                    switch (info.type) {
                        case PointerEventTypes.POINTERDOWN: onPointerDown(info); break;
                        case PointerEventTypes.POINTERMOVE: onPointerMove(info); break;
                        case PointerEventTypes.POINTERUP: onPointerUp(); break;
                    }
                });
            }
        }

        // ── Pointer events ───────────────────────────────────

        function findHandle(mesh: Mesh | null): Handle | null {
            if (!mesh) return null;
            return handles.find(h => h.mesh === mesh) ?? null;
        }

        function onPointerDown(info: PointerInfo): void {
            const pick = info.pickInfo;
            if (!pick?.hit || !pick.pickedMesh) return;

            // Prefer layout handles even if occluded
            let handle: Handle | null = null;
            if (scene) {
                const hits = scene.multiPick(scene.pointerX, scene.pointerY,
                    m => !!m && m.isPickable && !!(m.metadata as Record<string, unknown> | null)?.layoutHandle);
                if (hits && hits.length > 0 && hits[0].pickedMesh) {
                    handle = findHandle(hits[0].pickedMesh as Mesh);
                }
            }
            if (!handle) handle = findHandle(pick.pickedMesh as Mesh);
            if (!handle) return;

            const sel = getSelectedLayout();
            if (!sel) return;

            // Capture undo snapshot and cache node/rotation for drag
            dragLayout = sel.layout;
            dragNode = sel.node;
            dragRotMatrix = getWorldRotationMatrix(sel.node);
            dragBeforeData = sel.layout.getData();

            activeHandle = handle;
            handle.mesh.material = matActive!;
            dragStartSize = { x: sel.size.x, y: sel.size.y, z: sel.size.z };

            if (pick.pickedPoint) {
                dragStartPoint = pick.pickedPoint.clone();
            }

            // Suspend camera
            const camera = scene?.activeCamera;
            const canvas = scene?.getEngine().getRenderingCanvas();
            if (camera && canvas) {
                cameraWasAttached = true;
                camera.detachControl();
            }
        }

        function onPointerMove(info: PointerInfo): void {
            // Hover effects when not dragging
            if (!activeHandle) {
                const pick = info.pickInfo;
                const pickedMesh = pick?.pickedMesh as Mesh | null;
                const hoveredHandle = findHandle(pickedMesh);
                for (const h of handles) {
                    h.mesh.material = (h === hoveredHandle) ? matHover! : matNormal!;
                }
                return;
            }

            // Dragging
            if (!dragStartPoint || !dragStartSize || !scene || !dragLayout || !dragNode || !dragRotMatrix) return;

            const delta = calculateDelta(scene, activeHandle.config, dragStartPoint, dragNode, dragRotMatrix);
            if (!delta) return;

            // Apply delta to size
            const config = activeHandle.config;
            let newW = dragStartSize.x;
            let newH = dragStartSize.y;
            let newD = dragStartSize.z;

            for (let i = 0; i < config.axes.length; i++) {
                switch (config.axes[i]) {
                    case 'X': newW = Math.max(MIN_SIZE, dragStartSize.x + delta.x); break;
                    case 'Y': newH = Math.max(MIN_SIZE, dragStartSize.y + delta.y); break;
                    case 'Z': newD = Math.max(MIN_SIZE, dragStartSize.z + delta.z); break;
                }
            }

            // Commit to layout — only force Fixed on axes that actually changed
            const axes = new Set(config.axes);
            const patch: Record<string, unknown> = {
                width: newW, height: newH, depth: newD,
            };
            if (axes.has('X')) patch.widthMode = 'Fixed';
            if (axes.has('Y')) patch.heightMode = 'Fixed';
            if (axes.has('Z')) patch.depthMode = 'Fixed';
            void dragLayout.commit(patch);

            // Notify UI (behaviors panel) of the change
            const traceScene = getUndoManager().traceScene;
            const so = traceScene?.findSceneObjectByTransform(dragNode);
            if (so) {
                dispatchBehaviorChanged({
                    objectPath: so.getPath(),
                    behaviorType: 'Layout',
                    action: 'updated',
                });
            }

            // Update handle positions to follow the resizing object
            updateHandlePositions(newW, newH, newD, dragNode, dragRotMatrix);
        }

        function onPointerUp(): void {
            if (activeHandle) {
                activeHandle.mesh.material = matNormal!;

                // Push undo command
                if (dragLayout && dragBeforeData && !getUndoManager().isUndoRedoing) {
                    const afterData = dragLayout.getData();
                    const traceScene = getUndoManager().traceScene;
                    const so = traceScene?.findSceneObjectByTransform(
                        selectionService.selectedEntity as TransformNode
                    );
                    if (so) {
                        getUndoManager().push(new BehaviorCommitCommand(
                            so,
                            'Layout',
                            dragBeforeData,
                            afterData,
                            'width',
                        ));
                    }
                }
            }

            activeHandle = null;
            dragStartPoint = null;
            dragStartSize = null;
            dragLayout = null;
            dragNode = null;
            dragRotMatrix = null;
            dragBeforeData = null;

            // Restore camera
            const camera = scene?.activeCamera;
            const canvas = scene?.getEngine().getRenderingCanvas();
            if (camera && canvas && cameraWasAttached) {
                camera.attachControl(canvas, true);
                cameraWasAttached = false;
            }

            // Refresh handles at new positions
            sync();
        }

        // ── Drag plane projection ────────────────────────────

        function calculateDelta(s: Scene, config: HandleConfig, startPoint: Vector3, node: TransformNode, rotMatrix: Matrix): Vector3 | null {
            const camera = s.activeCamera;
            if (!camera) return null;

            const nodeWorldPos = node.getAbsolutePosition();

            // Cast ray from camera through current pointer
            const ray = s.createPickingRay(s.pointerX, s.pointerY, null, camera);

            // For 3-axis corner handles, use camera-facing plane
            const cameraDir = camera.getForwardRay().direction;
            const planeNormal = cameraDir;

            // Project dragStartPoint onto the plane through nodeWorldPos
            const startToPlane = nodeWorldPos.subtract(startPoint);
            const startProjection = Vector3.Dot(startToPlane, planeNormal);
            const projectedStart = startPoint.add(planeNormal.scale(startProjection));

            // Intersect current ray with the plane
            const denom = Vector3.Dot(ray.direction, planeNormal);
            if (Math.abs(denom) < 0.001) return null;

            const t = Vector3.Dot(nodeWorldPos.subtract(ray.origin), planeNormal) / denom;
            const currentPoint = ray.origin.add(ray.direction.scale(t));

            // World delta, projected onto the object's local axes (rotation-aware)
            const worldDelta = currentPoint.subtract(projectedStart);
            const result = Vector3.Zero();

            // Local axis directions rotated into world space
            const localAxes = [Vector3.Right(), Vector3.Up(), new Vector3(0, 0, 1)];
            const worldAxes = localAxes.map(a => Vector3.TransformNormal(a, rotMatrix));

            for (let i = 0; i < config.axes.length; i++) {
                const axisIdx = config.axes[i] === 'X' ? 0 : config.axes[i] === 'Y' ? 1 : 2;
                const component = Vector3.Dot(worldDelta, worldAxes[axisIdx]) * config.directions[i];
                switch (config.axes[i]) {
                    case 'X': result.x = component; break;
                    case 'Y': result.y = component; break;
                    case 'Z': result.z = component; break;
                }
            }

            return result;
        }

        // ── Update handle positions during drag ──────────────

        function updateHandlePositions(w: number, h: number, d: number, node: TransformNode, rotMatrix: Matrix): void {
            const worldPos = node.getAbsolutePosition();

            for (let i = 0; i < handles.length && i < CORNER_CONFIGS.length; i++) {
                const config = CORNER_CONFIGS[i];
                const localOffset = new Vector3(
                    config.offset.x * w / 2,
                    config.offset.y * h / 2,
                    config.offset.z * d / 2,
                );
                handles[i].mesh.setAbsolutePosition(worldPos.add(Vector3.TransformNormal(localOffset, rotMatrix)));
            }
        }

        // ── Wire observers ───────────────────────────────────

        // Refresh handles when behavior data changes externally (e.g. from panel edits)
        const handleExternalChange = (event: Event) => {
            // Don't refresh during our own drag — we update handles directly
            if (activeHandle) return;
            const detail = (event as CustomEvent).detail as { behaviorType: string } | undefined;
            if (detail?.behaviorType === 'Layout' || detail?.behaviorType === '*') {
                sync();
            }
        };
        window.addEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, handleExternalChange);

        const modeObs = gizmoService.onGizmoModeChanged.add(sync);
        const selObs = selectionService.onSelectedEntityChanged.add(sync);
        sync();

        return {
            dispose: () => {
                modeObs?.remove();
                selObs?.remove();
                window.removeEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, handleExternalChange);
                pointerObserver?.remove();
                pointerObserver = null;
                clearHandles();
                matNormal?.dispose();
                matHover?.dispose();
                matActive?.dispose();
                matNormal = matHover = matActive = null;
            },
        };
    },
};
